/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import MMTCLOGO from "../../icons/MMTCLOGO.svg";
import GreenCheckLogo from "../../icons/GreenCheckLogo.svg";
import VectorTopRight from "../../icons/VectorTopRight.svg";
import VectorBottomLeft from "../../icons/VectorBottomLeft.svg";

// --- Sub-components ---
const SuccessSection = () => {
  return (
    <div className='flex flex-col items-center gap-4'>
      <div className='w-[120px] h-[120px] flex items-center justify-center'>
        <GreenCheckLogo className='w-full h-full' />
      </div>
      <div className='flex flex-col items-center gap-2'>
        <h2 className='text-[28px] font-bold text-black'>
          Account Added Successfully
        </h2>
        <p className='text-base text-[#666666] text-center'>
          Your account has been added successfully! You&rsquo;re all set to move
          forward.
        </p>
      </div>
    </div>
  );
};

const LoanInfoRow = ({ label, value, customClassName }) => {
  return (
    <div className='flex flex-col'>
      <span className='text-sm text-[#999999]'>{label}</span>
      <span className={`text-black ${customClassName}`}>{value}</span>
    </div>
  );
};

const LiquidityBox = ({ label, value, isLiquidityOverage }) => {
  let colorClass = "text-black";

  if (isLiquidityOverage === true) {
    colorClass = "text-[#007D06]";
  } else if (isLiquidityOverage === false) {
    colorClass = "text-[#C5140D]";
  }

  return (
    <div className='flex-1 bg-[#EEF3FF] border border-[#3861FB3D] rounded-lg p-4 flex flex-col gap-2'>
      <span className='text-xs text-[#666666] leading-tight'>{label}</span>
      <span className={`text-lg font-bold ${colorClass}`}>{value || "$0"}</span>
    </div>
  );
};

const LoanInfoCard = ({ apiData }) => {
  const {
    loanId,
    borrowerNames,
    propertyAddresses,
    borrowerLiquidity,
    totalLiquidity,
    shortageOrOverage,
    isLiquidityOverage,
    totalLoanAmount
  } = apiData;

  return (
    <div className='w-full bg-white border border-[#E5E5E5] rounded-2xl shadow-[0px_8px_30px_rgba(0,0,0,0.08)] p-8 flex flex-col gap-6'>
      {/* Row 1: Loan ID & Loan Amount */}
      <div className='flex justify-between items-center self-stretch'>
        <LoanInfoRow
          label='Loan ID:'
          value={loanId}
          customClassName='text-2xl font-semibold'
        />
        <LoanInfoRow
          label='Loan Amount:'
          value={totalLoanAmount}
          customClassName='text-2xl font-semibold'
        />
      </div>

      {/* Divider */}
      <div className='h-px bg-[#EEEEEE] self-stretch' />

      {/* Row 2: Borrower & Property Address */}
      <div className='flex justify-between items-start self-stretch'>
        <LoanInfoRow
          label='Borrower'
          value={borrowerNames?.join(", ") || "N/A"}
        />
        <LoanInfoRow
          label='Property Address'
          value={propertyAddresses?.[0] || "N/A"}
        />
      </div>

      {/* Divider */}
      <div className='h-px bg-[#EEEEEE] self-stretch' />

      {/* Row 3: Liquidity Stats Boxes */}
      <div className='flex gap-4 self-stretch'>
        <LiquidityBox
          label='Total Borrower Liquidity ($)'
          value={borrowerLiquidity}
        />
        <LiquidityBox
          label='Total Liquidity Required ($)'
          value={totalLiquidity}
        />
        <LiquidityBox
          label={
            isLiquidityOverage ? "Liquidity Overage" : "Liquidity Shortage"
          }
          value={shortageOrOverage}
          isLiquidityOverage={isLiquidityOverage}
        />
      </div>
    </div>
  );
};

// --- Main Component ---
export const AccountAddedSuccessfully = ({ apiData }) => {
  return (
    <div className='min-h-screen bg-white flex flex-col relative overflow-hidden font-sans'>
      {/* Background Decorative Vectors */}
      <VectorTopRight className='absolute top-0 right-0 pointer-events-none' />
      <VectorBottomLeft className='absolute bottom-0 left-0 pointer-events-none' />

      <div className='flex flex-col items-center pt-[90px] z-10'>
        <MMTCLOGO className='w-[148px] h-[49px]' />
      </div>

      <div className='flex flex-col items-center mt-8 z-10'>
        <div className='flex flex-col items-center gap-6 w-full max-w-[691px] px-4'>
          <SuccessSection />
          <LoanInfoCard apiData={apiData} />
        </div>
      </div>
    </div>
  );
};

AccountAddedSuccessfully.propTypes = {
  apiData: PropTypes.object.isRequired
};
